
import { useState, useEffect, useCallback, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { PlusCircle, Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ServiceModal } from "@/components/services/ServiceModal";
import { ContractFormValues } from "../ContractFormSchema";
import { Service, ContractService } from "@/lib/types/services";
import { useServices } from "@/hooks/useServices";
import { useCurrencies } from "@/hooks/useCurrencies";
import { formatCurrency } from "@/lib/formatters";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { toast } from "sonner";

interface ContractServicesProps {
  form: UseFormReturn<ContractFormValues>;
  contractId?: string;
  contractTypeId?: string;
  currencyId?: string;
  onTotalCostChange?: (total: number) => void;
  isTemporary?: boolean;
  onApplyCallback?: (callback: () => Promise<void>) => void;
}

export function ContractServicesSection({
  form,
  contractId,
  contractTypeId,
  currencyId,
  onTotalCostChange,
  isTemporary = false,
  onApplyCallback,
}: ContractServicesProps) {
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<string, {type: string, data: any}>>(new Map());
  const [retryCount, setRetryCount] = useState(0);
  const [isProcessingChanges, setIsProcessingChanges] = useState(false);

  const {
    useServicesForContractTypeQuery,
    useContractServicesQuery,
    useAddServiceToContractMutation,
    useRemoveServiceFromContractMutation,
    useUpdateContractServiceMutation,
  } = useServices();

  const { useCurrencyQuery, useCurrenciesQuery } = useCurrencies();
  const { data: contractCurrency } = useCurrencyQuery(currencyId || "");
  const { data: currencies = [] } = useCurrenciesQuery();
  
  const { exchangeRates } = useExchangeRates();
  
  // Memoize the exchange rate function to prevent unnecessary recalculations
  const getExchangeRate = useCallback((fromCurrencyId?: string, toCurrencyId?: string): number => {
    if (!fromCurrencyId || !toCurrencyId || fromCurrencyId === toCurrencyId) {
      return 1; // Same currency or missing info
    }
    
    const rateObj = exchangeRates.find(rate => 
      rate.base_currency_id === fromCurrencyId && 
      rate.target_currency_id === toCurrencyId
    );
    
    if (rateObj) {
      return rateObj.rate;
    }
    
    const reverseRateObj = exchangeRates.find(rate => 
      rate.base_currency_id === toCurrencyId && 
      rate.target_currency_id === fromCurrencyId
    );
    
    if (reverseRateObj && reverseRateObj.rate !== 0) {
      return 1 / reverseRateObj.rate;
    }
    
    return 1; // Default if no rate found
  }, [exchangeRates]);

  const { data: availableServices = [] } = useServicesForContractTypeQuery(
    contractTypeId || ""
  );
  
  // Optimize the query with proper caching, retry and error handling
  const { 
    data: contractServices = [], 
    refetch: refetchContractServices,
    isError: isContractServicesError,
    isFetching: isContractServicesFetching
  } = useContractServicesQuery(isTemporary ? "" : (contractId || ""), {
    enabled: !isTemporary && !!contractId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    onError: (error) => {
      toast.error("Failed to load contract services");
      console.error("Error fetching contract services:", error);
    }
  });

  const addServiceMutation = useAddServiceToContractMutation();
  const removeServiceMutation = useRemoveServiceFromContractMutation();
  const updateServiceMutation = useUpdateContractServiceMutation();

  // Memoize the displayed services to avoid unnecessary recalculations
  const displayServices = useMemo(() => {
    if (isTemporary) {
      return [];
    }
    
    return contractServices.map(service => {
      const pendingChange = pendingChanges.get(service.id);
      if (pendingChange && pendingChange.type === 'update') {
        return {...service, ...pendingChange.data};
      }
      return service;
    }).filter(service => {
      const pendingChange = pendingChanges.get(service.id);
      return !(pendingChange && pendingChange.type === 'remove');
    });
  }, [isTemporary, contractServices, pendingChanges]);
  
  // Memoize all display services including pending additions
  const allDisplayServices = useMemo(() => {
    const result = [...displayServices];
    
    pendingChanges.forEach((change, id) => {
      if (change.type === 'add' && !result.some(s => s.id === id)) {
        result.push(change.data);
      }
    });
    
    return result;
  }, [displayServices, pendingChanges]);
  
  // Memoize the total cost calculation
  const totalCost = useMemo(() => {
    return allDisplayServices.reduce((sum, contractService) => {
      if (!contractService.service) return sum;
      
      const serviceCost = contractService.service.cost;
      const serviceQuantity = contractService.quantity;
      const serviceCurrencyId = contractService.service.currency_id;
      
      if (serviceCurrencyId === currencyId) {
        return sum + serviceCost * serviceQuantity;
      }
      
      const rate = getExchangeRate(serviceCurrencyId, currencyId);
      return sum + serviceCost * serviceQuantity * rate;
    }, 0);
  }, [allDisplayServices, currencyId, getExchangeRate]);

  useEffect(() => {
    if (onTotalCostChange) {
      onTotalCostChange(totalCost);
    }
  }, [totalCost, onTotalCostChange]);

  const handleAddService = () => {
    setSelectedServiceId(null);
    setServiceModalOpen(true);
  };

  const handleServiceCreated = useCallback(async (service: Service) => {
    if (isTemporary) {
      if (!allDisplayServices.some(cs => cs.service_id === service.id)) {
        const tempService: ContractService = {
          id: `temp-${Date.now()}`,
          contract_id: contractId || '',
          service_id: service.id,
          service: service,
          quantity: 1,
          user_id: '',
          created_at: new Date().toISOString(),
        };
        
        setPendingChanges(prev => {
          const updated = new Map(prev);
          updated.set(tempService.id, { type: 'add', data: tempService });
          return updated;
        });
      }
    } else if (!contractServices.some(cs => cs.service_id === service.id)) {
      const tempService: ContractService = {
        id: `pending-add-${Date.now()}`,
        contract_id: contractId || '',
        service_id: service.id,
        service: service,
        quantity: 1,
        user_id: '',
        created_at: new Date().toISOString(),
      };
      
      setPendingChanges(prev => {
        const updated = new Map(prev);
        updated.set(tempService.id, { type: 'add', data: tempService });
        return updated;
      });
    }
  }, [allDisplayServices, contractId, contractServices, isTemporary]);

  const handleQuantityChange = useCallback(async (
    contractServiceId: string,
    currentQuantity: number,
    increment: boolean
  ) => {
    const newQuantity = increment ? currentQuantity + 1 : Math.max(1, currentQuantity - 1);
    
    setPendingChanges(prev => {
      const updated = new Map(prev);
      const existingChange = updated.get(contractServiceId);
      
      if (existingChange && existingChange.type === 'update') {
        updated.set(contractServiceId, { 
          type: 'update', 
          data: { ...existingChange.data, quantity: newQuantity }
        });
      } else if (existingChange && existingChange.type === 'add') {
        updated.set(contractServiceId, {
          type: 'add',
          data: { ...existingChange.data, quantity: newQuantity }
        });
      } else {
        const service = contractServices.find(cs => cs.id === contractServiceId);
        if (service) {
          updated.set(contractServiceId, { 
            type: 'update', 
            data: { quantity: newQuantity }
          });
        }
      }
      return updated;
    });
  }, [contractServices]);

  const handleRemoveService = useCallback(async (contractServiceId: string) => {
    setPendingChanges(prev => {
      const updated = new Map(prev);
      
      if (contractServiceId.startsWith('pending-add-')) {
        updated.delete(contractServiceId);
      } else {
        updated.set(contractServiceId, { type: 'remove', data: null });
      }
      
      return updated;
    });
  }, []);

  // Improved apply pending changes function with proper error handling
  const applyPendingChanges = useCallback(async () => {
    if (!contractId || isProcessingChanges || pendingChanges.size === 0) return;
    
    try {
      setIsProcessingChanges(true);
      
      // Process changes in batches to avoid overwhelming the API
      const addChanges = Array.from(pendingChanges.entries())
        .filter(([_, change]) => change.type === 'add');
        
      const updateChanges = Array.from(pendingChanges.entries())
        .filter(([_, change]) => change.type === 'update');
        
      const removeChanges = Array.from(pendingChanges.entries())
        .filter(([_, change]) => change.type === 'remove');
      
      // Process removals first
      for (const [id] of removeChanges) {
        await removeServiceMutation.mutateAsync(id);
      }
      
      // Then process updates
      for (const [id, change] of updateChanges) {
        await updateServiceMutation.mutateAsync({
          id,
          quantity: change.data.quantity,
        });
      }
      
      // Finally process additions
      for (const [_, change] of addChanges) {
        await addServiceMutation.mutateAsync({
          contractId,
          serviceId: change.data.service_id,
          quantity: change.data.quantity,
        });
      }
      
      // Clear pending changes after all operations complete successfully
      setPendingChanges(new Map());
      await refetchContractServices();
      toast.success("Contract services updated successfully");
      
    } catch (error) {
      console.error("Error applying service changes:", error);
      toast.error("Failed to update contract services. Please try again.");
      
      // Increment retry count and limit retries
      setRetryCount(prev => {
        if (prev >= 3) {
          toast.error("Maximum retry attempts reached. Please reload the page.");
          return prev;
        }
        return prev + 1;
      });
    } finally {
      setIsProcessingChanges(false);
    }
  }, [contractId, pendingChanges, isProcessingChanges, addServiceMutation, updateServiceMutation, removeServiceMutation, refetchContractServices]);

  // Expose the applyPendingChanges function to the parent component only when needed
  useEffect(() => {
    if (onApplyCallback && !isTemporary) {
      onApplyCallback(applyPendingChanges);
    }
  }, [onApplyCallback, isTemporary, applyPendingChanges]);

  // Show error state if needed
  if (isContractServicesError && !isTemporary && contractId) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium">Services</h3>
          <p className="text-sm text-muted-foreground">
            Add services to this contract
          </p>
        </div>
        <div className="rounded-md border border-destructive p-4 bg-destructive/10">
          <p className="text-destructive">
            Error loading contract services. Please reload the page and try again.
          </p>
        </div>
      </div>
    );
  }

  // Extract the service list UI to a separate function for clarity
  const renderServicesList = () => {
    if (allDisplayServices.length === 0) {
      return (
        <div className="rounded-md border border-dashed p-8 text-center">
          <p className="text-muted-foreground">No services added to this contract yet</p>
        </div>
      );
    }

    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Original Cost</TableHead>
              <TableHead>Contract Cost</TableHead>
              <TableHead>Quantity</TableHead>
              <TableHead>Total</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allDisplayServices.map((contractService: ContractService) => {
              const service = contractService.service;
              if (!service) return null;

              const serviceCurrency = service.currency || 
                currencies.find(c => c.id === service.currency_id);
              
              const exchangeRate = getExchangeRate(service.currency_id, currencyId);
              const convertedCost = service.cost * exchangeRate;
              
              const totalInContractCurrency = convertedCost * contractService.quantity;

              return (
                <TableRow key={contractService.id}>
                  <TableCell className="font-medium">{service.name}</TableCell>
                  <TableCell>{service.service_type}</TableCell>
                  <TableCell>
                    {formatCurrency(service.cost, serviceCurrency?.code || "USD")}
                  </TableCell>
                  <TableCell>
                    {serviceCurrency?.id !== contractCurrency?.id ? (
                      <>{formatCurrency(convertedCost, contractCurrency?.code || "USD")}</>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() =>
                          handleQuantityChange(
                            contractService.id,
                            contractService.quantity,
                            false
                          )
                        }
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span>{contractService.quantity}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() =>
                          handleQuantityChange(
                            contractService.id,
                            contractService.quantity,
                            true
                          )
                        }
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    {formatCurrency(
                      totalInContractCurrency,
                      contractCurrency?.code || "USD"
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleRemoveService(contractService.id)}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        
        <div className="p-4 flex justify-between items-center border-t">
          <div>
            <span className="font-medium">Total (in contract currency): </span>
            {formatCurrency(totalCost, contractCurrency?.code || "USD")}
          </div>
          {!isTemporary && pendingChanges.size > 0 && (
            <div className="text-sm text-muted-foreground italic">
              Changes will be applied when you save the contract
            </div>
          )}
        </div>
      </div>
    );
  };

  // Main render function
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Services</h3>
        <p className="text-sm text-muted-foreground">
          Add services to this contract
        </p>
      </div>

      <div>
        {isContractServicesFetching && !allDisplayServices.length ? (
          <div className="rounded-md border border-dashed p-8 text-center">
            <p className="text-muted-foreground">Loading contract services...</p>
          </div>
        ) : (
          renderServicesList()
        )}
      </div>

      <div className="flex justify-end">
        <Button 
          type="button" 
          onClick={handleAddService} 
          className="flex items-center"
          disabled={isContractServicesFetching && !allDisplayServices.length}
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Service
        </Button>
      </div>

      <ServiceModal
        open={serviceModalOpen}
        onOpenChange={setServiceModalOpen}
        onSuccess={handleServiceCreated}
        contextType="contract-edit"
        contractId={contractId}
      />
    </div>
  );
}
